import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import clsx from "clsx";
import { NOTE_FADE_MASK } from "./constants";
import type { MouseEvent } from "react";
import type { MarkerNoteTextProps, MarkerNoteTextState } from "./types";

// Keeps the Show more toggle and the wheel over the expanded text from reaching the plan.
const stopPlanInteraction = (event: Pick<Event, "stopPropagation">) => {
  event.stopPropagation();
};

export const MarkerNoteText = ({
  content,
  pillColor,
  pillTextColor,
}: MarkerNoteTextProps) => {
  const { t } = useTranslation();

  const textRef = useRef<HTMLParagraphElement>(null);

  // Starts clamped so the first measurement can tell whether the text overflows.
  const [textState, setTextState] = useState<MarkerNoteTextState>({
    kind: "clamped",
  });

  const toggleExpanded = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      stopPlanInteraction(event);
      if (textState.kind === "expanded") {
        // Otherwise the clamped text would keep the scroll offset and show the middle of the note.
        if (textRef.current !== null) textRef.current.scrollTop = 0;
        setTextState({ kind: "clamped" });
        return;
      }
      setTextState({ kind: "expanded" });
    },
    [textState.kind],
  );

  const isClamped = textState.kind === "clamped";
  const isExpanded = textState.kind === "expanded";

  useLayoutEffect(() => {
    const text = textRef.current;
    if (text === null) return;
    if (text.scrollHeight <= text.clientHeight) setTextState({ kind: "fits" });
  }, []);

  // The plan zooms from a native wheel listener, so stopping the wheel takes a native listener too.
  useEffect(() => {
    const text = textRef.current;
    if (!isExpanded || text === null) return;
    text.addEventListener("wheel", stopPlanInteraction, { passive: true });
    return () => text.removeEventListener("wheel", stopPlanInteraction);
  }, [isExpanded]);

  return (
    <div
      className={clsx(
        "rounded-lg bg-white px-3 pt-2.5",
        textState.kind === "fits" ? "pb-2.5" : "pb-2",
      )}
    >
      <p
        ref={textRef}
        className={clsx(
          "m-0 whitespace-pre-wrap break-words text-sm leading-5 text-gray-900",
          isClamped && "line-clamp-3",
          isExpanded &&
            "pointer-events-auto max-h-40 overflow-y-auto overscroll-contain",
        )}
        style={
          isClamped
            ? { maskImage: NOTE_FADE_MASK, WebkitMaskImage: NOTE_FADE_MASK }
            : undefined
        }
      >
        {content}
      </p>
      {textState.kind !== "fits" && (
        <div
          className={clsx("flex justify-end", isClamped ? "mt-1" : "mt-1.5")}
        >
          <button
            type="button"
            className="pointer-events-auto inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold leading-4 hover:brightness-95"
            style={{ backgroundColor: pillColor, color: pillTextColor }}
            onMouseDown={stopPlanInteraction}
            onTouchStart={stopPlanInteraction}
            onClick={toggleExpanded}
          >
            {isClamped
              ? t("smartSurveyor.elementDetails.note.showMore")
              : t("smartSurveyor.elementDetails.note.showLess")}
            {isClamped ? (
              <ChevronDown size={12} strokeWidth={2.5} aria-hidden="true" />
            ) : (
              <ChevronUp size={12} strokeWidth={2.5} aria-hidden="true" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};
